package api

import com.apollographql.apollo3.ApolloClient
import com.apollographql.apollo3.api.ApolloResponse
import com.apollographql.apollo3.api.Query
import config.ApiConfig
import graphql.GetAllTradeNewsQuery
import graphql.GetTradeNewsByCategoryQuery
import graphql.GetTradeNewsItemQuery
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import models.NewsItem

@Serializable
data class BackendHealthResponse(
    val status: String
)

class GraphQLClientException(message: String, cause: Throwable? = null) : Exception(message, cause)

interface TradeNewsApiClient {
    suspend fun fetchNewsItems(category: String? = null): List<NewsItem>
    suspend fun fetchNewsItem(id: String): NewsItem?
    suspend fun fetchHealthStatus(): BackendHealthResponse
    fun close() {}
}

class GraphQLClient(
    private val apolloClient: ApolloClient,
    private val healthClient: HttpClient,
    private val endpoint: String,
) : TradeNewsApiClient {

    override suspend fun fetchNewsItems(category: String?): List<NewsItem> {
        return if (category == null) {
            executeQuery(
                operation = GetAllTradeNewsQuery(),
                context = "trade news",
            ) { data -> data.tradeNews.map { it.toNewsItem() } }
        } else {
            executeQuery(
                operation = GetTradeNewsByCategoryQuery(category),
                context = "category news",
            ) { data -> data.tradeNewsByCategory.map { it.toNewsItem() } }
        }
    }

    override suspend fun fetchNewsItem(id: String): NewsItem? {
        return executeQuery(
            operation = GetTradeNewsItemQuery(id),
            context = "news item",
        ) { data -> data.tradeNewsItem?.toNewsItem() }
    }

    override suspend fun fetchHealthStatus(): BackendHealthResponse {
        return try {
            healthClient.get(buildHealthEndpoint(endpoint)).body()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw GraphQLClientException(
                "Backend unavailable: ${e.message ?: "unknown error"}",
                e
            )
        }
    }

    override fun close() {
        apolloClient.close()
        healthClient.close()
    }

    private suspend fun <D : Query.Data, T> executeQuery(
        operation: Query<D>,
        context: String,
        map: (D) -> T,
    ): T {
        val response: ApolloResponse<D> = try {
            apolloClient.query(operation).execute()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw GraphQLClientException(
                "Failed to fetch $context: ${e.message ?: "unknown error"}",
                e
            )
        }

        val errors = response.errors
        if (!errors.isNullOrEmpty()) {
            throw GraphQLClientException(errors.joinToString(", ") { it.message })
        }

        val data = response.data
            ?: throw GraphQLClientException("GraphQL response did not contain $context data")

        return map(data)
    }

    companion object {
        /**
         * Production 向けファクトリ。endpoint を 1 度だけ正規化し、Apollo 側の `serverUrl` と
         * internal に保持する `endpoint` の両方に同じ値を渡す。Copilot 指摘の重複呼び出し解消。
         */
        operator fun invoke(endpoint: String = ApiConfig.apiEndpoint): GraphQLClient {
            val normalized = normalizeGraphQLEndpoint(endpoint)
            return GraphQLClient(
                apolloClient = ApolloClient.Builder().serverUrl(normalized).build(),
                healthClient = createDefaultHealthClient(),
                endpoint = normalized,
            )
        }

        private fun createDefaultHealthClient(): HttpClient {
            return HttpClient(Js) {
                install(ContentNegotiation) {
                    json(Json {
                        prettyPrint = true
                        isLenient = true
                        ignoreUnknownKeys = true
                    })
                }
            }
        }
    }
}

private fun GetAllTradeNewsQuery.TradeNew.toNewsItem() = NewsItem(
    id = id,
    title = title,
    description = description,
    link = link,
    source = source,
    publishedAt = publishedAt,
    category = category,
    titleJa = titleJa,
    descriptionJa = descriptionJa,
    translationStatus = translationStatus,
    translatedAt = translatedAt,
)

private fun GetTradeNewsByCategoryQuery.TradeNewsByCategory.toNewsItem() = NewsItem(
    id = id,
    title = title,
    description = description,
    link = link,
    source = source,
    publishedAt = publishedAt,
    category = category,
    titleJa = titleJa,
    descriptionJa = descriptionJa,
    translationStatus = translationStatus,
    translatedAt = translatedAt,
)

private fun GetTradeNewsItemQuery.TradeNewsItem.toNewsItem() = NewsItem(
    id = id,
    title = title,
    description = description,
    link = link,
    source = source,
    publishedAt = publishedAt,
    category = category,
    titleJa = titleJa,
    descriptionJa = descriptionJa,
    translationStatus = translationStatus,
    translatedAt = translatedAt,
    summaryJa = summaryJa,
    summaryStatus = summaryStatus,
    summarizedAt = summarizedAt,
)

fun buildHealthEndpoint(apiEndpoint: String): String {
    val trimmedEndpoint = apiEndpoint.trimEnd('/')

    return if (trimmedEndpoint.endsWith("/graphql")) {
        trimmedEndpoint.removeSuffix("/graphql") + "/health"
    } else {
        "$trimmedEndpoint/health"
    }
}

/**
 * 末尾 `/` 付き endpoint（例: `.../graphql/`）を Apollo に渡すと backend が `/graphql` の
 * ルーティングだけ受け付ける場合に 404 になりうる。health 側 [buildHealthEndpoint] は
 * `trimEnd('/')` で吸収しているが、GraphQL 側の不整合を防ぐため default constructor でも
 * 同じ正規化を噛ませる。
 */
internal fun normalizeGraphQLEndpoint(raw: String): String = raw.trimEnd('/')
